import { useEffect, useRef, useState } from 'react';

type UseTabActivityOptions = {
  onFocus?: () => void;
  onBlur?: () => void;
};

const isTabVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

export function useTabActivity({ onFocus, onBlur }: UseTabActivityOptions) {
  const [isActive, setIsActive] = useState(isTabVisible);

  // Call sites pass inline arrows (`onFocus: () => refetch()`), so reading the
  // callbacks through refs keeps the listeners registered once for the lifetime
  // of the hook instead of being torn down and re-added on every render.
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;

  // Mirrors `isActive` for the listeners, which must not close over the state.
  const isActiveRef = useRef(isActive);

  useEffect(() => {
    // Returning to a backgrounded tab fires BOTH `focus` and `visibilitychange`,
    // which used to run onFocus — and whatever refetch it triggers — twice per
    // return. Both listeners are still needed (`focus`/`blur` alone miss a tab
    // switch inside the same window; `visibilitychange` alone misses focus moving
    // to another window while the tab stays visible), so the transition is
    // deduplicated here instead: only an actual change of state notifies.
    const setActive = (next: boolean) => {
      if (isActiveRef.current === next) return;
      isActiveRef.current = next;
      setIsActive(next);
      if (next) {
        onFocusRef.current?.();
      } else {
        onBlurRef.current?.();
      }
    };

    const handleFocus = () => setActive(true);
    const handleBlur = () => setActive(false);
    const handleVisibilityChange = () => setActive(isTabVisible());

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isActive;
}
